import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CcFundingMerchantMapEntry {
  id: string;
  tenantId: string;
  tenantName?: string | null;
  locationId: string;
  locationName?: string | null;
  mid: string;
  merchantLabel: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type CcFundingDriveSyncStatus = 'ingested' | 'duplicate' | 'unmapped' | 'error';

export interface CcFundingDriveSyncLogEntry {
  id: string;
  driveFileId: string;
  fileName: string;
  mid: string;
  status: CcFundingDriveSyncStatus;
  tenantId: string | null;
  locationId: string | null;
  uploadId: string | null;
  message: string | null;
  processedAt: string;
}

export interface CcFundingDriveSyncResult {
  filesSeen: number;
  ingested: number;
  duplicate: number;
  unmapped: number;
  errors: number;
}

export type CcFundingDriveCredentialsSource = 'database' | 'environment' | 'none';

export interface CcFundingDriveCredentialsStatus {
  configured: boolean;
  source: CcFundingDriveCredentialsSource;
  folderId: string;
  updatedAt: string | null;
}

export interface ManualUploadMidGroupResult {
  mid: string;
  mapped: boolean;
  tenantId?: string;
  locationId?: string;
  duplicate?: boolean;
  uploadId?: string;
  sourceRowsInserted?: number;
  fundingRowsInserted?: number;
  message?: string;
}

export interface ManualUploadResult {
  fileName: string;
  midGroups: ManualUploadMidGroupResult[];
}

export interface CreateCcFundingMerchantMapInput {
  tenantId: string;
  locationId: string;
  mid: string;
  merchantLabel?: string;
}

export interface UpdateCcFundingMerchantMapInput {
  id: string;
  tenantId?: string;
  locationId?: string;
  mid?: string;
  merchantLabel?: string | null;
  isActive?: boolean;
}

// ── Query Keys ─────────────────────────────────────────────────────────────────

const MERCHANT_MAP_KEY = 'admin-cc-funding-merchant-map';
const SYNC_LOG_KEY = 'admin-cc-funding-drive-sync-log';
const CREDENTIALS_KEY = 'admin-cc-funding-drive-credentials';

// ── Queries ────────────────────────────────────────────────────────────────────

export function useCcFundingMerchantMap(params?: { tenantId?: string }) {
  return useQuery({
    queryKey: [MERCHANT_MAP_KEY, params],
    queryFn: () =>
      api.get<{ data: CcFundingMerchantMapEntry[] }>(
        '/admin/cc-funding-drive/merchant-map',
        params as Record<string, string | undefined>,
      ),
  });
}

export function useCcFundingDriveSyncLog(params?: { page?: number; perPage?: number; status?: string }) {
  return useQuery({
    queryKey: [SYNC_LOG_KEY, params],
    queryFn: () =>
      api.get<{ data: CcFundingDriveSyncLogEntry[]; pagination: any }>(
        '/admin/cc-funding-drive/sync-log',
        params as Record<string, string | number | undefined>,
      ),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export function useCreateCcFundingMerchantMap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCcFundingMerchantMapInput) =>
      api.post<{ data: CcFundingMerchantMapEntry }>('/admin/cc-funding-drive/merchant-map', data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MERCHANT_MAP_KEY] });
    },
  });
}

export function useUpdateCcFundingMerchantMap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: UpdateCcFundingMerchantMapInput) =>
      api.put<{ data: CcFundingMerchantMapEntry }>(`/admin/cc-funding-drive/merchant-map/${id}`, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MERCHANT_MAP_KEY] });
    },
  });
}

export function useDeleteCcFundingMerchantMap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ data: { deleted: boolean } }>(`/admin/cc-funding-drive/merchant-map/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MERCHANT_MAP_KEY] });
    },
  });
}

export function useTriggerCcFundingDriveSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ data: CcFundingDriveSyncResult }>('/admin/cc-funding-drive/sync-now'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SYNC_LOG_KEY] });
    },
  });
}

export function useUploadCcFundingDriveFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch('/api/v1/admin/cc-funding-drive/manual-upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
        throw error;
      }
      const body = await response.json();
      return body.data as ManualUploadResult;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SYNC_LOG_KEY] });
    },
  });
}

export function useCcFundingDriveCredentialsStatus() {
  return useQuery({
    queryKey: [CREDENTIALS_KEY],
    queryFn: () => api.get<{ data: CcFundingDriveCredentialsStatus }>('/admin/cc-funding-drive/credentials'),
  });
}

export function useSaveCcFundingDriveCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { serviceAccountKeyJson: string; folderId?: string }) =>
      api.post<{ data: CcFundingDriveCredentialsStatus }>('/admin/cc-funding-drive/credentials', data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [CREDENTIALS_KEY] });
    },
  });
}

export function useClearCcFundingDriveCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ data: CcFundingDriveCredentialsStatus }>('/admin/cc-funding-drive/credentials'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [CREDENTIALS_KEY] });
    },
  });
}
